// Typed client for the IG Markets endpoints:
//   /api/IgTrading/settings?demo=  — per-user IG creds (demo or live) + kill switch
//   /api/IgTrading/enabled         — flip the per-user IG LIVE kill switch
//   /api/IgTrading/account?demo=   — IG account (proves the connection)
// API key + password are masked on read; send blank to keep the stored value.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export interface IgSettings {
  identifier: string;
  accountId: string;
  isDemo: boolean;
  apiKeyMasked: string;
  passwordMasked: string;
  liveTradingEnabled: boolean;
  updatedAt: string | null;
}

export interface UpdateIgSettings {
  apiKey?: string;      // blank keeps the stored key
  identifier?: string;
  password?: string;    // blank keeps the stored password
  accountId?: string;
  liveTradingEnabled?: boolean;
}

export interface IgAccount {
  accountNumber: string;
  status: string;
  equity: number | null;
  cash: number | null;
  buyingPower: number | null;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error((await res.text()) || `Request failed (${res.status})`);
  return res.json() as Promise<T>;
}

export const igTradingService = {
  getSettings: (demo: boolean) => getJson<IgSettings>(`/api/IgTrading/settings?demo=${demo}`),

  saveSettings: async (demo: boolean, req: UpdateIgSettings): Promise<IgSettings> => {
    const res = await apiFetch(`${API_BASE}/api/IgTrading/settings?demo=${demo}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!res.ok) throw new Error((await res.text()) || `Request failed (${res.status})`);
    return res.json() as Promise<IgSettings>;
  },

  setEnabled: async (enabled: boolean): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/api/IgTrading/enabled?enabled=${enabled}`, { method: 'POST' });
    if (!res.ok) throw new Error((await res.text()) || `Request failed (${res.status})`);
  },

  // Throws on 404 (no creds) / 5xx — the caller treats any failure as "not connected".
  getAccount: (demo: boolean) => getJson<IgAccount>(`/api/IgTrading/account?demo=${demo}`),

  placeOrder: async (
    demo: boolean,
    req: { symbol: string; side: 'buy' | 'sell'; quantity: number; assetClass?: 'equity' | 'forex' },
  ): Promise<{ orderId: string }> => {
    const res = await apiFetch(`${API_BASE}/api/IgTrading/orders?demo=${demo}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!res.ok) throw new Error((await res.text()) || `Request failed (${res.status})`);
    return res.json() as Promise<{ orderId: string }>;
  },

  closePosition: async (demo: boolean, symbol: string, assetClass?: 'equity' | 'forex'): Promise<void> => {
    const qs = `demo=${demo}${assetClass ? `&assetClass=${assetClass}` : ''}`;
    const res = await apiFetch(`${API_BASE}/api/IgTrading/positions/${encodeURIComponent(symbol)}/close?${qs}`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error((await res.text()) || `Request failed (${res.status})`);
  },
};
